import { SIXTEENTHS } from "../movement/step.ts";

/**
 * Chat's rules beside the text rule (ADR-068 decisions 1, 3 and 4): how far Local reaches, the limits the
 * gateway and the map server hold chat to, and the closed set of reasons a refusal names. The numbers are the
 * ADR's defaults, which its open question 3 asks the author to confirm.
 */

/**
 * How far a Local line reaches, in tiles, by the straight line between the speaker's feet and a listener's
 * (Bible 9.1: a 50 m, 50 tile, radius).
 */
export const LOCAL_CHAT_RADIUS_TILES = 50;

/**
 * Local's reach in sixteenths, squared, the unit positions are compared in.
 */
const HEARING_SQUARED = (LOCAL_CHAT_RADIUS_TILES * SIXTEENTHS) ** 2;

/**
 * How many `say` and `whisper` messages one account may send in a window of `CHAT_WINDOW_MS`, whatever its
 * connections; past it a message is refused `rate` with the wait until the window reopens.
 */
export const CHAT_MESSAGES_PER_WINDOW = 8;

/**
 * The account's chat window, in milliseconds.
 */
export const CHAT_WINDOW_MS = 10_000;

/**
 * How many blocks and unblocks one account may make in a minute, since each writes a row.
 */
export const BLOCK_CHANGES_PER_MINUTE = 10;

/**
 * How many Local lines the map server hands one listener in a second beside the listener's own; past it a line
 * is not delivered to that listener and is counted, and everyone else still hears it.
 */
export const LOCAL_LINES_PER_SECOND = 10;

/**
 * How many whispers reach one connection in a second; past it the sender is refused `busy`.
 */
export const WHISPERS_RECEIVED_PER_SECOND = 5;

/**
 * Every reason a `chatRefused` may name (ADR-068 decision 3), which the page words: the account's limit of
 * messages and its limit of block changes, each with the wait until it reopens; the text rule's three; a
 * whisper's recipient not in the world (or blocking the sender), the sender itself, a family the sender
 * blocked, or over its cap; a block naming no family, one's own, a full list, an unblock naming no family on
 * the list, a dev join, which has no family, and a list that could not be written. Its amendment "Global, Trade
 * and Help" adds eight: a channel that is none of the three, one the character is not in, a mute covering it
 * (with the time left), the channel's pace and the repeat guard (each with its wait), a mute the GM's roles do not
 * allow, one longer than they allow, and a lift that lifted nothing; `changes`, `family`, `own`, `account` and `unavailable` answer a join, a
 * leave or a mute as they answer a block. The emote wheel adds two (ADR-114 decision 6.1): `emote`, for an id the
 * server's table lacks or the account does not hold, one reason for both so that asking reveals nothing about sets,
 * and `target`, for an action aimed at a target that may not be named. Party chat adds one (ADR-101 decision 25):
 * `alone`, a party line from a character in no party.
 */
export const CHAT_REFUSALS = ["rate", "changes", "empty", "long", "characters", "absent", "self", "blocked", "busy", "family", "own", "full", "unlisted", "account", "unavailable", "channel", "unjoined", "muted", "pace", "repeated", "forbidden", "duration", "unmuted", "emote", "target", "alone"] as const;

/**
 * One reason a `chatRefused` names.
 */
export type ChatRefusal = (typeof CHAT_REFUSALS)[number];

const REFUSALS: ReadonlySet<string> = new Set(CHAT_REFUSALS);

/**
 * Whether a word is one of the refusal reasons this build knows. A page meeting a reason it does not know, from
 * a later server, shows a sentence of its own for it.
 *
 * @param word The word.
 * @returns True for a known reason.
 */
export function isChatRefusal(word: string): word is ChatRefusal {
  return REFUSALS.has(word);
}

/**
 * Whether a listener hears a speaker in Local: its feet within `LOCAL_CHAT_RADIUS_TILES` of the speaker's by
 * the straight line, the edge included.
 *
 * @param speakerX The speaker's feet across, in sixteenths.
 * @param speakerY The speaker's feet down, in sixteenths.
 * @param listenerX The listener's feet across, in sixteenths.
 * @param listenerY The listener's feet down, in sixteenths.
 * @returns True when the listener hears the speaker.
 * @example
 * withinHearing(0, 0, 480, 640);
 * // true: 30 and 40 tiles apart, 50 by the straight line
 */
export function withinHearing(speakerX: number, speakerY: number, listenerX: number, listenerY: number): boolean {
  const dx = listenerX - speakerX;
  const dy = listenerY - speakerY;
  return dx * dx + dy * dy <= HEARING_SQUARED;
}
